<template>
  <div class="login-page">
    <div v-if="errorMsg" class="error-tip">
      <font-awesome-icon icon="exclamation-circle" />
      <span>{{ errorMsg }}</span>
    </div>

    <div class="bg-decoration">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <div class="login-main">
      <div class="login-card">
        <div class="logo-section">
          <img :src="logoImage" alt="树苗英语" class="logo-image" />
        </div>

        <form class="login-form" @submit.prevent="handleLogin">
          <div class="input-group">
            <div class="input-icon">
              <font-awesome-icon icon="user" />
            </div>
            <input
              v-model="loginForm.username"
              type="text"
              placeholder="请输入用户名"
              class="input-field"
            />
          </div>

          <div class="input-group">
            <div class="input-icon">
              <font-awesome-icon icon="lock" />
            </div>
            <input
              v-model="loginForm.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="请输入密码"
              class="input-field"
            />
            <button type="button" class="toggle-password" @click="showPassword = !showPassword">
              <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
            </button>
          </div>

          <button type="submit" class="login-btn" :class="{ loading: isLoading }">
            <span v-if="!isLoading" class="btn-text">登录</span>
            <span v-else class="loading-dots">
              <span></span><span></span><span></span>
            </span>
          </button>
        </form>

        <div class="account-actions" aria-label="账号辅助操作">
          <button
            type="button"
            class="account-action-link account-action-button"
            @click="openForgotUsernameModal"
          >
            忘记用户名
          </button>
          <span class="account-action-divider" aria-hidden="true"></span>
          <button
            type="button"
            class="account-action-link account-action-button"
            @click="openForgotPasswordModal"
          >
            忘记密码
          </button>
          <span class="account-action-divider" aria-hidden="true"></span>
          <button
            type="button"
            class="account-action-link account-action-button"
            @click="goRegister"
          >
            立即注册
          </button>
        </div>
      </div>
    </div>

    <footer class="page-footer">
      <div class="footer-socials" aria-label="社交平台入口">
        <button type="button" class="social-link social-button" aria-label="微信" @click="openWechatModal">
          <img :src="weixinIcon" alt="" class="social-icon-img" />
          <span>微信</span>
        </button>
        <span class="social-divider" aria-hidden="true"></span>
        <button type="button" class="social-link social-button" aria-label="小红书" @click="openXhsModal">
          <img :src="xhsIcon" alt="" class="social-icon-img" />
          <span>小红书</span>
        </button>
        <span class="social-divider" aria-hidden="true"></span>
        <button type="button" class="social-link social-button" aria-label="抖音" @click="openDouyinModal">
          <img :src="douyinIcon" alt="" class="social-icon-img" />
          <span>抖音</span>
        </button>
      </div>
      <p class="footer-icp">蜀ICP备2026022176号</p>
    </footer>

    <Transition name="modal-fade">
      <div
        v-if="isForgotUsernameModalOpen"
        class="social-modal-mask"
        @click="closeForgotUsernameModal"
      >
        <section class="social-modal" @click.stop>
          <button
            type="button"
            class="social-modal-close"
            aria-label="关闭"
            @click="closeForgotUsernameModal"
          >
            <font-awesome-icon icon="xmark" />
          </button>

          <header class="social-modal-header">
            <h2 class="social-modal-title">忘记用户名</h2>
            <p class="social-modal-subtitle">请输入您的专属注册链接</p>
          </header>

          <div class="social-modal-body">
            <div v-if="!forgotUsernameResult" class="lookup-block">
              <label class="lookup-label" for="register-link-input">链接</label>
              <div class="lookup-input-shell">
                <input
                  id="register-link-input"
                  v-model.trim="registerLink"
                  type="text"
                  placeholder="粘贴链接到此处"
                  class="lookup-input"
                />
                <button
                  type="button"
                  class="lookup-side-btn"
                  aria-label="粘贴链接"
                  title="粘贴链接"
                  @click="pasteRegisterLink"
                >
                  <font-awesome-icon icon="paste" />
                </button>
              </div>
            </div>

            <button
              v-if="!forgotUsernameResult"
              type="button"
              class="lookup-submit-btn"
              :class="{ active: canSubmitForgotUsername }"
              :disabled="!canSubmitForgotUsername"
              @click="submitForgotUsername"
            >
              查询用户名
            </button>

            <div v-else class="lookup-result-card" role="status" aria-live="polite">
              <div class="lookup-result-main">
                <span class="lookup-result-check">
                  <font-awesome-icon icon="check" />
                </span>
                <p class="lookup-result-text">您的用户名是: {{ forgotUsernameResult }}</p>
              </div>
              <button
                type="button"
                class="lookup-result-action"
                aria-label="复制用户名"
                title="复制用户名"
                @click="copyForgotUsername"
              >
                <font-awesome-icon icon="copy" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </Transition>

    <Transition name="modal-fade">
      <div
        v-if="isForgotPasswordModalOpen"
        class="social-modal-mask"
        @click="closeForgotPasswordModal"
      >
        <section class="social-modal" @click.stop>
          <button
            type="button"
            class="social-modal-close"
            aria-label="关闭"
            @click="closeForgotPasswordModal"
          >
            <font-awesome-icon icon="xmark" />
          </button>

          <header class="social-modal-header">
            <h2 class="social-modal-title">忘记密码</h2>
            <p class="social-modal-subtitle">请输入您的用户名和邮箱进行验证</p>
          </header>

          <div class="social-modal-body">
            <div class="lookup-block">
              <label class="lookup-label" for="verify-username-input">用户名</label>
              <div class="lookup-input-shell lookup-input-shell-simple">
                <input
                  id="verify-username-input"
                  v-model.trim="forgotPasswordForm.username"
                  type="text"
                  placeholder="请输入用户名"
                  class="lookup-input"
                />
              </div>
            </div>

            <div class="lookup-block lookup-block-spaced">
              <label class="lookup-label" for="verify-email-input">邮箱</label>
              <div class="lookup-input-shell lookup-input-shell-simple">
                <input
                  id="verify-email-input"
                  v-model.trim="forgotPasswordForm.email"
                  type="email"
                  placeholder="请输入邮箱"
                  class="lookup-input"
                />
              </div>
            </div>

            <button
              type="button"
              class="lookup-submit-btn"
              :class="{ active: canSubmitForgotPassword }"
              :disabled="!canSubmitForgotPassword"
              @click="submitForgotPassword"
            >
              验证
            </button>
          </div>
        </section>
      </div>
    </Transition>

    <Transition name="modal-fade">
      <div
        v-if="isResetPasswordModalOpen"
        class="social-modal-mask"
        @click="closeResetPasswordModal"
      >
        <section class="social-modal" @click.stop>
          <button
            type="button"
            class="social-modal-close"
            aria-label="关闭"
            @click="closeResetPasswordModal"
          >
            <font-awesome-icon icon="xmark" />
          </button>

          <header class="social-modal-header">
            <h2 class="social-modal-title">重置密码</h2>
            <p class="social-modal-subtitle">请设置新密码</p>
          </header>

          <div class="social-modal-body">
            <div class="lookup-block">
              <label class="lookup-label" for="reset-password-input">新密码</label>
              <div class="lookup-input-shell lookup-input-shell-password">
                <input
                  id="reset-password-input"
                  v-model="resetPasswordForm.password"
                  :type="showResetPassword ? 'text' : 'password'"
                  placeholder="请输入新密码"
                  class="lookup-input"
                />
                <button
                  type="button"
                  class="lookup-side-btn lookup-side-btn-minimal"
                  :aria-label="showResetPassword ? '隐藏密码' : '显示密码'"
                  :title="showResetPassword ? '隐藏密码' : '显示密码'"
                  @click="showResetPassword = !showResetPassword"
                >
                  <font-awesome-icon :icon="showResetPassword ? 'eye-slash' : 'eye'" />
                </button>
              </div>
            </div>

            <div class="lookup-block lookup-block-spaced">
              <label class="lookup-label" for="reset-confirm-password-input">确认密码</label>
              <div class="lookup-input-shell lookup-input-shell-simple">
                <input
                  id="reset-confirm-password-input"
                  v-model="resetPasswordForm.confirmPassword"
                  type="password"
                  placeholder="请再次输入新密码"
                  class="lookup-input"
                />
              </div>
            </div>

            <button
              type="button"
              class="lookup-submit-btn"
              :class="{ active: canSubmitResetPassword }"
              :disabled="!canSubmitResetPassword"
              @click="submitResetPassword"
            >
              重置密码
            </button>
          </div>
        </section>
      </div>
    </Transition>

    <Transition name="modal-fade">
      <div
        v-if="isResetPasswordSuccessModalOpen"
        class="social-modal-mask"
        @click="closeResetPasswordSuccessModal"
      >
        <section class="social-modal" @click.stop>
          <button
            type="button"
            class="social-modal-close"
            aria-label="关闭"
            @click="closeResetPasswordSuccessModal"
          >
            <font-awesome-icon icon="xmark" />
          </button>

          <header class="social-modal-header social-modal-header-success">
            <h2 class="social-modal-title">已修改密码，请登录</h2>
          </header>

          <div class="social-modal-body social-modal-body-success">
            <div class="password-success-icon-wrap" aria-hidden="true">
              <div class="password-success-icon">
                <font-awesome-icon icon="check" />
              </div>
            </div>

            <p class="password-success-text">密码重置成功!</p>

            <button
              type="button"
              class="lookup-submit-btn lookup-submit-btn-dark active"
              @click="closeResetPasswordSuccessModal"
            >
              关闭
            </button>
          </div>
        </section>
      </div>
    </Transition>

    <Transition name="modal-fade">
      <div v-if="isWechatModalOpen" class="social-modal-mask" @click="closeWechatModal">
        <section class="social-modal" @click.stop>
          <button type="button" class="social-modal-close" aria-label="关闭" @click="closeWechatModal">
            <font-awesome-icon icon="xmark" />
          </button>

          <header class="social-modal-header">
            <h2 class="social-modal-title">微信</h2>
            <p class="social-modal-subtitle">有任何问题，可随时联系我</p>
          </header>

          <div class="social-modal-body">
            <div class="social-id-row">
              <span class="social-id-label">微信号：</span>
              <span class="social-id-value">{{ wechatId }}</span>
              <button
                type="button"
                class="copy-btn"
                aria-label="复制微信号"
                title="复制微信号"
                @click="copyWechatId"
              >
                <font-awesome-icon :icon="wechatCopied ? 'check' : 'copy'" />
              </button>
            </div>

            <div class="social-qr-frame">
              <img :src="wechatQrImage" alt="微信二维码" class="social-qr-image social-qr-image-square" />
            </div>
          </div>
        </section>
      </div>
    </Transition>

    <Transition name="modal-fade">
      <div v-if="isXhsModalOpen" class="social-modal-mask" @click="closeXhsModal">
        <section class="social-modal" @click.stop>
          <button type="button" class="social-modal-close" aria-label="关闭" @click="closeXhsModal">
            <font-awesome-icon icon="xmark" />
          </button>

          <header class="social-modal-header">
            <h2 class="social-modal-title">小红书</h2>
            <p class="social-modal-subtitle">有任何问题，可随时联系我</p>
          </header>

          <div class="social-modal-body">
            <div class="social-id-row">
              <span class="social-id-label">小红书号：</span>
              <span class="social-id-value">{{ xhsId }}</span>
              <button
                type="button"
                class="copy-btn"
                aria-label="复制小红书号"
                title="复制小红书号"
                @click="copyXhsId"
              >
                <font-awesome-icon :icon="xhsCopied ? 'check' : 'copy'" />
              </button>
            </div>

            <div class="social-qr-frame social-qr-frame-wide">
              <img :src="xhsQrImage" alt="小红书二维码" class="social-qr-image social-qr-image-wide" />
            </div>
          </div>
        </section>
      </div>
    </Transition>

    <Transition name="modal-fade">
      <div v-if="isDouyinModalOpen" class="social-modal-mask" @click="closeDouyinModal">
        <section class="social-modal" @click.stop>
          <button type="button" class="social-modal-close" aria-label="关闭" @click="closeDouyinModal">
            <font-awesome-icon icon="xmark" />
          </button>

          <header class="social-modal-header">
            <h2 class="social-modal-title">抖音</h2>
            <p class="social-modal-subtitle">有任何问题，可随时联系我</p>
          </header>

          <div class="social-modal-body">
            <div class="social-id-row">
              <span class="social-id-label">抖音号：</span>
              <span class="social-id-value">{{ douyinId }}</span>
              <button
                type="button"
                class="copy-btn"
                aria-label="复制抖音号"
                title="复制抖音号"
                @click="copyDouyinId"
              >
                <font-awesome-icon :icon="douyinCopied ? 'check' : 'copy'" />
              </button>
            </div>

            <div class="social-qr-frame social-qr-frame-douyin">
              <img :src="douyinQrImage" alt="抖音二维码" class="social-qr-image social-qr-image-douyin" />
            </div>
          </div>
        </section>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import douyinIcon from '../assets/douyin.svg'
import douyinQrImage from '../assets/douyin.jpg'
import logoImage from '../assets/logo.png'
import wechatQrImage from '../assets/weixin.jpg'
import weixinIcon from '../assets/weixin.svg'
import xhsIcon from '../assets/xhs.svg'
import xhsQrImage from '../assets/xhs.jpg'
import * as UserApi from '@/api/user'
import { setToken } from '@/config/axios/auth'
import { showFailToast } from 'vant'

const router = useRouter()
const showPassword = ref(false)
const isLoading = ref(false)
const errorMsg = ref('')

const registerLink = ref('')
const forgotUsernameResult = ref('')
const forgotPasswordForm = ref({
  username: '',
  email: '',
})
const resetPasswordForm = ref({
  username: '',
  password: '',
  verificationCode: '',
  confirmPassword: '',
})

const isForgotUsernameModalOpen = ref(false)
const isForgotPasswordModalOpen = ref(false)
const isResetPasswordModalOpen = ref(false)
const isResetPasswordSuccessModalOpen = ref(false)
const isWechatModalOpen = ref(false)
const isXhsModalOpen = ref(false)
const isDouyinModalOpen = ref(false)
const showResetPassword = ref(false)

const wechatCopied = ref(false)
const xhsCopied = ref(false)
const douyinCopied = ref(false)

const wechatId = 'Lwww8520'
const xhsId = 'TreeSapling'
const douyinId = 'TreeSapling'

const loginForm = ref({
  username: '',
  password: '',
})

const canSubmitForgotUsername = computed(() => Boolean(registerLink.value.trim()))
const canSubmitForgotPassword = computed(
  () => Boolean(forgotPasswordForm.value.username.trim() && forgotPasswordForm.value.email.trim())
)
const canSubmitResetPassword = computed(
  () => Boolean(resetPasswordForm.value.password && resetPasswordForm.value.confirmPassword)
)

const clearErrorLater = () => {
  window.setTimeout(() => {
    errorMsg.value = ''
  }, 3000)
}

const showToast = (message) => {
  errorMsg.value = message
  clearErrorLater()
}

const handleLogin = async () => {
  errorMsg.value = ''

  if (!loginForm.value.username.trim()) {
    showToast('请输入用户名')
    return
  }

  if (!loginForm.value.password.trim()) {
    showToast('请输入密码')
    return
  }

  isLoading.value = true
  try {
    const res = await UserApi.login(loginForm.value)
    if (res.accessToken && res.refreshToken) {
      setToken(res)
    }
    await router.push('/')
  } catch {
    // 接口错误已在 axios 拦截器中统一提示
  } finally {
    isLoading.value = false
  }
}

const closeAllModals = () => {
  isForgotUsernameModalOpen.value = false
  isForgotPasswordModalOpen.value = false
  isResetPasswordModalOpen.value = false
  isResetPasswordSuccessModalOpen.value = false
  isWechatModalOpen.value = false
  isXhsModalOpen.value = false
  isDouyinModalOpen.value = false
}

const openForgotUsernameModal = () => {
  closeAllModals()
  registerLink.value = ''
  forgotUsernameResult.value = ''
  isForgotUsernameModalOpen.value = true
}

const closeForgotUsernameModal = () => {
  isForgotUsernameModalOpen.value = false
  forgotUsernameResult.value = ''
}

const pasteRegisterLink = async () => {
  try {
    const clipboardText = await navigator.clipboard.readText()
    if (!clipboardText.trim()) {
      showToast('剪贴板里没有可用链接')
      return
    }
    registerLink.value = clipboardText.trim()
  } catch {
    showToast('无法读取剪贴板，请手动粘贴链接')
  }
}

const submitForgotUsername = async() => {
  try {
    const username = await UserApi.forgotUsername(registerLink.value)
    if(username !=='') {
      forgotUsernameResult.value = username
    } else {
      showFailToast('链接不存在')
    }
  } catch {
    // 接口错误已在 axios 拦截器中统一提示
  }
  
}

const copyForgotUsername = async () => {
  if (!forgotUsernameResult.value) {
    return
  }

  try {
    await navigator.clipboard.writeText(forgotUsernameResult.value)
    showToast('已复制')
  } catch {
    showToast('复制失败，请手动复制用户名')
  }
}

const openForgotPasswordModal = () => {
  closeAllModals()
  forgotPasswordForm.value = {
    username: '',
    email: ''
  }
  resetPasswordForm.value = {
    password: '',
    confirmPassword: ''
  }
  showResetPassword.value = false
  isForgotPasswordModalOpen.value = true
}

const closeForgotPasswordModal = () => {
  isForgotPasswordModalOpen.value = false
}

const openResetPasswordModal = (username, verificationCod) => {
  closeAllModals()
  resetPasswordForm.value = {
    username: username,
    password: '',
    confirmPassword: '',
    verificationCode: verificationCod
  }
  showResetPassword.value = false
  isResetPasswordModalOpen.value = true
}

const closeResetPasswordModal = () => {
  isResetPasswordModalOpen.value = false
  showResetPassword.value = false
}

const openResetPasswordSuccessModal = () => {
  closeAllModals()
  isResetPasswordSuccessModalOpen.value = true
}

const closeResetPasswordSuccessModal = () => {
  isResetPasswordSuccessModalOpen.value = false
}

const submitForgotPassword = async() => {
  try {
    const verificationCode = await UserApi.resetPasswordValidate(forgotPasswordForm.value)
    closeForgotPasswordModal()
    openResetPasswordModal(forgotPasswordForm.value.username, verificationCode)
  } catch {
    // 接口错误已在 axios 拦截器中统一提示
  }

 
}

const submitResetPassword = async() => {
  if (!canSubmitResetPassword.value) {
    showFailToast('请输入新密码并确认密码')
    return
  }

  if (resetPasswordForm.value.password !== resetPasswordForm.value.confirmPassword) {
    showFailToast('两次输入的密码不一致')
    return
  }
  try {
    await UserApi.resetPassword(resetPasswordForm.value)
    closeResetPasswordModal()
    openResetPasswordSuccessModal()
  } catch {
    // 接口错误已在 axios 拦截器中统一提示
  }
  
}

const goRegister = () => {
  closeAllModals()
  router.push('/register')
}

const openWechatModal = () => {
  wechatCopied.value = false
  closeAllModals()
  isWechatModalOpen.value = true
}

const closeWechatModal = () => {
  isWechatModalOpen.value = false
}

const openXhsModal = () => {
  xhsCopied.value = false
  closeAllModals()
  isXhsModalOpen.value = true
}

const closeXhsModal = () => {
  isXhsModalOpen.value = false
}

const openDouyinModal = () => {
  douyinCopied.value = false
  closeAllModals()
  isDouyinModalOpen.value = true
}

const closeDouyinModal = () => {
  isDouyinModalOpen.value = false
}

const copyWechatId = async () => {
  try {
    await navigator.clipboard.writeText(wechatId)
    wechatCopied.value = true
    window.setTimeout(() => {
      wechatCopied.value = false
    }, 1800)
  } catch {
    showToast('复制失败，请手动复制微信号')
  }
}

const copyXhsId = async () => {
  try {
    await navigator.clipboard.writeText(xhsId)
    xhsCopied.value = true
    window.setTimeout(() => {
      xhsCopied.value = false
    }, 1800)
  } catch {
    showToast('复制失败，请手动复制小红书号')
  }
}

const copyDouyinId = async () => {
  try {
    await navigator.clipboard.writeText(douyinId)
    douyinCopied.value = true
    window.setTimeout(() => {
      douyinCopied.value = false
    }, 1800)
  } catch {
    showToast('复制失败，请手动复制抖音号')
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #fdfbf7 0%, #f5f0e8 50%, #faf8f5 100%);
  padding: 20px 20px 0;
  position: relative;
  overflow: hidden;
}

.bg-decoration {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  animation: float-orb 8s ease-in-out infinite;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #ffe4d4 0%, #ffd4c4 100%);
  top: -100px;
  right: -100px;
  animation-delay: 0s;
}

.orb-2 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #e8f4ff 0%, #d4e8ff 100%);
  bottom: -50px;
  left: -100px;
  animation-delay: -3s;
}

.orb-3 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #ffeaa8 0%, #ffd88a 100%);
  top: 50%;
  left: 30%;
  animation-delay: -5s;
}

@keyframes float-orb {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  33% {
    transform: translate(30px, -30px) scale(1.05);
  }

  66% {
    transform: translate(-20px, 20px) scale(0.95);
  }
}

.login-main {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0 36px;
}

.login-card {
  width: 100%;
  max-width: 448px;
  padding: 48px 40px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.5) inset;
  position: relative;
  z-index: 10;
  animation: card-appear 0.8s ease-out;
}

@keyframes card-appear {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.logo-section {
  text-align: center;
  margin-bottom: 32px;
}

.logo-image {
  display: block;
  width: min(240px, 78%);
  height: auto;
  margin: 0 auto;
  object-fit: contain;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(245, 240, 232, 0.8);
  border-radius: 16px;
  padding: 4px;
  transition: all 0.3s ease;
}

.input-group:focus-within {
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 0 2px rgba(255, 138, 61, 0.3);
}

.input-icon {
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
  font-size: 15px;
  flex-shrink: 0;
}

.input-group:focus-within .input-icon {
  color: #ff8a3d;
}

.input-field {
  flex: 1;
  border: none;
  background: transparent;
  padding: 14px 0;
  font-size: 15px;
  color: #2d3436;
  outline: none;
}

.input-field::placeholder {
  color: #aaa;
}

.toggle-password {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #aaa;
  cursor: pointer;
  transition: color 0.3s;
}

.toggle-password:hover {
  color: #666;
}

.login-btn {
  width: 100%;
  height: 56px;
  padding: 16px;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  border: none;
  border-radius: 16px;
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 24px rgba(255, 138, 61, 0.35);
  animation: breathe 3s ease-in-out infinite;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes breathe {
  0%,
  100% {
    box-shadow: 0 8px 24px rgba(255, 138, 61, 0.35);
  }

  50% {
    box-shadow: 0 8px 32px rgba(255, 138, 61, 0.5);
  }
}

.login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(255, 138, 61, 0.5);
}

.login-btn:active {
  transform: translateY(0);
}

.login-btn.loading {
  pointer-events: none;
}

.loading-dots {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 50%;
  animation: bounce-dot 1.4s ease-in-out infinite both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce-dot {
  0%,
  80%,
  100% {
    transform: scale(0);
  }

  40% {
    transform: scale(1);
  }
}

.account-actions {
  margin-top: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  color: #7d7d7d;
  font-size: 14px;
}

.account-action-link {
  color: #7d7d7d;
  text-decoration: none;
  transition: color 0.25s ease;
}

.account-action-button {
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
}

.account-action-link:hover {
  color: #5f5044;
}

.account-action-divider {
  width: 1px;
  height: 16px;
  background: rgba(125, 125, 125, 0.2);
}

.error-tip {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: rgba(45, 52, 54, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  animation: toast-in 0.4s ease-out;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.page-footer {
  width: calc(100% + 40px);
  margin-left: -20px;
  padding: 22px 20px 12px;
  background: transparent;
  color: #4f5358;
  position: relative;
  z-index: 10;
}

.footer-socials {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  flex-wrap: wrap;
}

.social-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #4f5358;
  text-decoration: none;
  font-size: 14px;
  line-height: 1;
  transition: opacity 0.25s ease;
}

.social-button {
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
}

.social-link:hover {
  opacity: 0.78;
}

.social-icon-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.social-divider {
  width: 1px;
  height: 16px;
  background: rgba(79, 83, 88, 0.14);
}

.footer-icp {
  margin-top: 18px;
  text-align: center;
  color: rgba(79, 83, 88, 0.62);
  font-size: 12px;
  letter-spacing: 0.5px;
}

.social-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(78, 65, 52, 0.18);
  backdrop-filter: blur(10px);
}

.social-modal {
  position: relative;
  width: min(680px, 100%);
  border-radius: 34px;
  background: linear-gradient(180deg, rgba(255, 251, 246, 0.98) 0%, rgba(249, 241, 231, 0.97) 100%);
  border: 1px solid rgba(255, 184, 136, 0.28);
  box-shadow:
    0 30px 60px rgba(140, 104, 68, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
  overflow: hidden;
}

.social-modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.56);
  color: #8f7b6a;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.social-modal-close:hover {
  background: rgba(255, 255, 255, 0.84);
  color: #6b594a;
}

.social-modal-header {
  padding: 34px 28px 22px;
  text-align: center;
  border-bottom: 1px solid rgba(186, 159, 133, 0.24);
}

.social-modal-header-success {
  padding-bottom: 28px;
}

.social-modal-title {
  font-size: 28px;
  line-height: 1.1;
  color: #3f342c;
  font-weight: 800;
}

.social-modal-subtitle {
  margin-top: 18px;
  font-size: 15px;
  color: #8d7c6d;
}

.social-modal-body {
  padding: 34px 28px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.social-modal-body-success {
  padding-top: 26px;
}

.lookup-block {
  width: 100%;
}

.lookup-block-spaced {
  margin-top: 22px;
}

.lookup-label {
  display: block;
  margin-bottom: 14px;
  color: #6f6054;
  font-size: 16px;
  font-weight: 700;
}

.lookup-input-shell {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 74px;
  border-radius: 20px;
  padding: 0 18px 0 20px;
  background: rgba(255, 255, 255, 0.52);
  box-shadow: inset 0 0 0 1px rgba(197, 169, 143, 0.32);
}

.lookup-input-shell:focus-within {
  background: rgba(255, 255, 255, 0.78);
  box-shadow: inset 0 0 0 2px rgba(255, 184, 136, 0.48);
}

.lookup-input-shell-simple {
  padding-right: 20px;
}

.lookup-input-shell-password {
  padding-right: 10px;
}

.lookup-input {
  flex: 1;
  border: none;
  background: transparent;
  color: #4f4137;
  font-size: 18px;
  outline: none;
}

.lookup-input::placeholder {
  color: #ab9b8d;
}

.lookup-side-btn {
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.75);
  color: #816f61;
  cursor: pointer;
  transition: all 0.25s ease;
}

.lookup-side-btn:hover {
  background: rgba(255, 255, 255, 0.95);
  color: #5f5044;
}

.lookup-side-btn-minimal {
  background: transparent;
  color: #a18f80;
  box-shadow: none;
}

.lookup-side-btn-minimal:hover {
  background: rgba(255, 255, 255, 0.55);
  color: #6f6054;
}

.lookup-textarea-shell {
  width: 100%;
  min-height: 108px;
  border-radius: 22px;
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.52);
  box-shadow: inset 0 0 0 1px rgba(197, 169, 143, 0.32);
}

.lookup-textarea-shell:focus-within {
  background: rgba(255, 255, 255, 0.78);
  box-shadow: inset 0 0 0 2px rgba(255, 184, 136, 0.48);
}

.lookup-textarea {
  width: 100%;
  min-height: 72px;
  border: none;
  resize: vertical;
  background: transparent;
  color: #4f4137;
  font-size: 18px;
  line-height: 1.55;
  outline: none;
}

.lookup-textarea::placeholder {
  color: #ab9b8d;
}

.lookup-submit-btn {
  width: 100%;
  min-height: 78px;
  margin-top: 24px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(180deg, #e6dfd6 0%, #d8cfc4 100%);
  color: rgba(50, 42, 35, 0.72);
  font-size: 20px;
  font-weight: 800;
  cursor: not-allowed;
  box-shadow: 0 12px 24px rgba(153, 120, 87, 0.08);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background 0.25s ease,
    color 0.25s ease;
}

.lookup-submit-btn.active {
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(255, 138, 61, 0.24);
}

.lookup-submit-btn.active:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 28px rgba(255, 138, 61, 0.32);
}

.lookup-result-card {
  width: 100%;
  min-height: 92px;
  padding: 0 20px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(233, 252, 242, 0.96) 0%, rgba(239, 253, 245, 0.92) 100%);
  box-shadow: inset 0 0 0 1px rgba(113, 237, 162, 0.65);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.lookup-result-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.lookup-result-check {
  flex-shrink: 0;
  color: #09b954;
  font-size: 20px;
}

.lookup-result-text {
  color: #0cad51;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.5;
  word-break: break-all;
}

.lookup-result-action {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.55);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #0cad51;
  font-size: 18px;
  opacity: 0.9;
  cursor: pointer;
  transition:
    background 0.25s ease,
    transform 0.25s ease,
    opacity 0.25s ease;
}

.lookup-result-action:hover {
  background: rgba(255, 255, 255, 0.82);
  opacity: 1;
  transform: translateY(-1px);
}

.lookup-submit-btn-dark {
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(255, 138, 61, 0.24);
}

.lookup-submit-btn-dark.active {
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  box-shadow: 0 12px 24px rgba(255, 138, 61, 0.24);
}

.lookup-submit-btn-dark.active:hover {
  box-shadow: 0 16px 28px rgba(255, 138, 61, 0.32);
}

.password-success-icon-wrap {
  width: 92px;
  height: 92px;
  border-radius: 50%;
  background: rgba(22, 196, 90, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-success-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #16c45a;
  color: #16c45a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.password-success-text {
  margin-top: 26px;
  color: #16c45a;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
}

.social-id-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  color: #8a7a6c;
  font-size: 18px;
}

.social-id-label {
  color: #96877a;
}

.social-id-value {
  color: #5f5044;
  font-weight: 700;
}

.copy-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.72);
  color: #877362;
  cursor: pointer;
  transition: all 0.25s ease;
}

.copy-btn:hover {
  background: rgba(255, 255, 255, 0.96);
  color: #5f5044;
}

.social-qr-frame {
  margin-top: 26px;
  padding: 14px;
  border-radius: 22px;
  background: #fffdf9;
  box-shadow:
    0 18px 34px rgba(126, 96, 69, 0.13),
    inset 0 0 0 1px rgba(224, 212, 199, 0.7);
}

.social-qr-frame-wide {
  padding: 10px;
}

.social-qr-frame-douyin {
  padding: 12px;
}

.social-qr-image {
  display: block;
  border-radius: 12px;
}

.social-qr-image-square {
  width: min(248px, 62vw);
  height: min(285px, 70vw);
}

.social-qr-image-wide {
  width: min(252px, 66vw);
  height: auto;
  border-radius: 16px;
}

.social-qr-image-douyin {
  width: min(252px, 66vw);
  height: auto;
  border-radius: 18px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .login-page {
    padding-left: 16px;
    padding-right: 16px;
  }

  .login-main {
    padding-top: 18px;
    padding-bottom: 28px;
  }

  .login-card {
    padding: 36px 28px;
    border-radius: 24px;
  }

  .page-footer {
    width: calc(100% + 32px);
    margin-left: -16px;
    padding: 18px 16px 10px;
  }

  .footer-socials {
    gap: 12px;
  }

  .social-link {
    font-size: 13px;
  }

  .social-icon-img {
    width: 22px;
    height: 22px;
  }

  .social-divider {
    height: 18px;
  }

  .social-modal-mask {
    padding: 12px;
  }

  .social-modal {
    border-radius: 28px;
  }

  .social-modal-header {
    padding: 26px 18px 18px;
  }

  .social-modal-title {
    font-size: 24px;
  }

  .social-modal-subtitle {
    margin-top: 14px;
    font-size: 14px;
  }

  .social-modal-body {
    padding: 24px 18px 28px;
  }

  .social-id-row {
    font-size: 16px;
  }

  .lookup-input-shell {
    min-height: 64px;
  }

  .lookup-input {
    font-size: 16px;
  }

  .lookup-textarea {
    font-size: 16px;
  }

  .lookup-submit-btn {
    min-height: 66px;
    font-size: 18px;
  }

  .social-modal-close {
    top: 14px;
    right: 14px;
    width: 36px;
    height: 36px;
  }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .bg-orb,
  .login-orb,
  .orb {
    display: none;
  }

  .login-card,
  .social-modal,
  .toast-message {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  .login-card {
    animation: none;
  }

  .brand-icon,
  .loading-dot {
    animation: none;
  }

  .login-btn,
  .lookup-submit-btn,
  .social-modal-close,
  .social-link {
    transition-property: background-color, color, border-color, box-shadow, transform, opacity;
  }
}
</style>
